import { useEffect, useState } from "react";
import {
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from "react-hook-form";
import * as yup from "yup";

import {
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
} from "../../../hooks";
import {
  IAddBankAccount,
  IBank,
  IOnboardingStatus,
  IOnboardingStep3Request,
  IOnboardingStepResponse,
  ISwAppSupportedCountry,
  ISwAppSupportedCurrency,
  NoMeta,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";
import { IOnboardingStepProps, IOnboardingStepViewProps } from "../Onboarding";

import { View } from "./View";

export interface IOnboardingStep3ViewProps
  extends IOnboardingStepViewProps<
    IOnboardingStep3Request & IBankAccountTagFormHelper
  > {
  isLoadingSupportedCountries: boolean;
  swAppSupportedCountries: ISwAppSupportedCountry[];

  isLoadingSupportedCurrencies: boolean;
  swAppSupportedCurrencies: ISwAppSupportedCurrency[];

  bank_account_tags: IAddBankAccount["bank_account_tags"];
  appendBankAccountTag: UseFieldArrayAppend<
    IAddBankAccount,
    "bank_account_tags"
  >;
  removeBankAccountTag: UseFieldArrayRemove;

  isLoadingBankList: boolean;
  bankList: IBank[];
  formValues: IOnboardingStep3Request & IBankAccountTagFormHelper;

  isBankAccountResolved: boolean;
  setBankAccountResolved: (isResolved: boolean) => void;
}

interface ICountrySelect {
  country: string;
}

interface ICountryAndCurrencySelect extends ICountrySelect {
  currency: string;
}

interface IBankAccountTagFormHelper {
  formHelperTagName?: string;
  formHelperTagDescription?: string | null;
}

export const OnboardingStep3 = (props: IOnboardingStepProps) => {
  const [isBankAccountResolved, setBankAccountResolved] = useState(false);

  const {
    isLoading: isLoadingSupportedCountries,
    // isSuccess: isSuccessSupportedCountries,
    data: swAppSupportedCountries,
  } = useMerchantApiQuery<ISwAppSupportedCountry[]>("getSupportedCountries");

  const {
    // isLoading: isLoadingOnboardingStatus,
    isSuccess: isSuccessOnboardingStatus,
    data: onboardingStatus,
  } = useMerchantApiQuery<IOnboardingStatus>("getOnboardingStatus");

  const {
    // isLoading: isLoadingStep3,
    isSuccess: isSuccessStep3,
    // isError: isErrorStep3,
    // data: apiResponse,
    mutate: saveOnboardingStep,
  } = useMerchantApiMutation<IOnboardingStep3Request, IOnboardingStepResponse>(
    "saveOnboardingStep3"
  ).usePostMutation;

  useEffect(() => {
    if (isSuccessOnboardingStatus) {
      onboardingStatus?.primary_bank_account &&
        reset({
          ...onboardingStatus.primary_bank_account,
          set_as_primary: true,
        });
    }
  }, [isSuccessOnboardingStatus]);

  useEffect(() => {
    if (isSuccessStep3) {
      reset();
      props.handleNext();
    }
  }, [isSuccessStep3]);

  const initialFormValues: IOnboardingStep3Request & IBankAccountTagFormHelper =
    {
      bank_account_tags: [],
      country: "",
      currency: "",
      bank_code: "",
      account_number: "",
      bvn: "",
      set_as_primary: true,

      formHelperTagName: "",
      formHelperTagDescription: null,
    };

  const onboardingStep3Schema: yup.SchemaOf<
    IOnboardingStep3Request & IBankAccountTagFormHelper
  > = yup
    .object({
      bank_account_tags: yup.array().of(
        yup.object().shape({
          bankAccount: yup.string().nullable().optional(),
          tag_name: yup.string().required("Tag name is required"),
          description: yup.string().nullable().optional(),
        })
      ),

      formHelperTagName: yup.string().optional(),
      formHelperTagDescription: yup.string().nullable().optional(),

      country: yup.string().required("Country is required"),
      currency: yup.string().required("Currency is required"),
      bank_code: yup.string().required("Bank is required"),
      account_number: yup.string().required("Account Number is required"),
      bvn: yup
        .string()
        .nullable()
        .optional()
        .when("country", {
          is: (country: string) => country === "NG",
          then: yup.string().required("BVN is required"),
        }),
      set_as_primary: yup
        .boolean()
        .required("Set as primary status is required"),
    })
    .required();

  const handleApiSubmit = (data: IOnboardingStep3Request) => {
    saveOnboardingStep(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, control, reset, watch } = useHookForm(
    {
      initialFormValues,
      validationSchema: onboardingStep3Schema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
    }
  );

  console.warn("form Errors: ", errors);

  const formValues = watch();

  const {
    isLoading: isLoadingBankList,
    // isSuccess: isSuccessBankList,
    data: bankList,
  } = useMerchantApiQueryWithParams<ICountryAndCurrencySelect, IBank[], NoMeta>(
    [
      "getBanks",
      {
        country: formValues.country,
        currency: formValues.currency,
      },
    ],
    {
      enabled: formValues.country !== "",
    }
  );

  const {
    isLoading: isLoadingSupportedCurrencies,
    data: swAppSupportedCurrencies,
  } = useMerchantApiQueryWithParams<
    ICountrySelect,
    ISwAppSupportedCurrency[],
    NoMeta
  >(
    [
      "getSupportedCurrencies",
      {
        country: formValues.country,
      },
    ],
    {
      enabled: formValues.country !== "",
    }
  );

  // TODO: resolve issue this also resets the existing value when onboardingStatus is retrieved
  // clear bank selection on country change
  // useEffect(() => {
  //   reset({ ...formValues, bank_code: "", currency: "" });
  // }, [formValues.country]);

  const {
    fields: bank_account_tags,
    append: appendBankAccountTag,
    remove: removeBankAccountTag,
  } = useFieldArray({ name: "bank_account_tags", control });

  return (
    <View
      {...props}
      formValues={formValues}
      bank_account_tags={bank_account_tags}
      appendBankAccountTag={appendBankAccountTag}
      removeBankAccountTag={removeBankAccountTag}
      isLoadingSupportedCountries={isLoadingSupportedCountries}
      swAppSupportedCountries={swAppSupportedCountries || []}
      isLoadingSupportedCurrencies={isLoadingSupportedCurrencies}
      swAppSupportedCurrencies={swAppSupportedCurrencies || []}
      isLoadingBankList={isLoadingBankList}
      bankList={bankList || []}
      isBankAccountResolved={isBankAccountResolved}
      setBankAccountResolved={setBankAccountResolved}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
